// Generates a downloadable PDF of LMS login cards (one per unique student),
// 10 cards per page (2 columns x 5 rows). Each card shows the LMS URL, the
// student's name (bold, larger), and their username/password.

import { jsPDF } from "jspdf";

const LMS_DOMAIN = "zebrarobotics.com";

// Layout (points; 72pt = 1in, Letter = 612 x 792).
const PAGE = { w: 612, h: 792 };
const MARGIN = 36; // 0.5in
const COLS = 2;
const ROWS = 5;
const COL_GAP = 14;
const ROW_GAP = 12;
const CARDS_PER_PAGE = COLS * ROWS;
const PAD = 14; // inner card padding
const BORDER_RGB = [41, 171, 226]; // #29ABE2

// Builds the password from the student id + first name, cased per `casing`:
//   "camel" -> 1545Raguman (first name Capitalized)  [default]
//   "lower" -> 1545raguman (all lowercase)
function buildPassword(id, firstname, casing) {
  if (casing === "lower") return `${id}${firstname}`.toLowerCase();
  const camel = firstname
    ? firstname.charAt(0).toUpperCase() + firstname.slice(1).toLowerCase()
    : "";
  return `${id}${camel}`;
}

function toCredentials(reports, casing) {
  const seen = new Set();
  const students = [];
  (reports || []).forEach((r) => {
    const id = r.student_id != null ? String(r.student_id).trim() : "";
    if (!id || seen.has(id)) return;
    seen.add(id);

    const name = (r.student_name || "").trim();
    const firstname = name.split(/\s+/)[0] || "";
    students.push({
      name,
      username: `${id}@${LMS_DOMAIN}`,
      password: buildPassword(id, firstname, casing),
    });
  });
  return students;
}

function drawCard(doc, student, x, y, w, h) {
  // Border
  doc.setDrawColor(BORDER_RGB[0], BORDER_RGB[1], BORDER_RGB[2]);
  doc.setLineWidth(2);
  doc.roundedRect(x, y, w, h, 5, 5, "S");

  const cx = x + w / 2;
  const left = x + PAD;
  const innerW = w - PAD * 2;
  let cursor = y + PAD + 6;

  doc.setTextColor(26, 26, 26);

  // Header
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Open the browser and search for:", cx, cursor, { align: "center" });
  cursor += 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("LMS.ZebraRobotics.com", cx, cursor, { align: "center" });
  cursor += 24;

  // A label (normal) + value run on one line, value optionally bold/larger.
  const line = (label, value, valueSize, valueBold) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(label, left, cursor);
    const labelW = doc.getTextWidth(label) + 4;

    doc.setFont("helvetica", valueBold ? "bold" : "normal");
    doc.setFontSize(valueSize);
    const valueLines = doc.splitTextToSize(value, innerW - labelW);
    doc.text(valueLines, left + labelW, cursor);
    cursor += 13 + (valueLines.length - 1) * (valueSize + 2);
  };

  line("Name:", student.name, 15, true);
  cursor += 8;
  line("Username:", student.username, 12, false);
  cursor += 6;
  line("Password:", student.password, 12, false);
}

export function downloadLmsCredentialCards(reports, branchName, casing = "camel") {
  const students = toCredentials(reports, casing);
  if (students.length === 0) return;

  const doc = new jsPDF({ unit: "pt", format: "letter" });

  const cardW = (PAGE.w - MARGIN * 2 - COL_GAP * (COLS - 1)) / COLS;
  const cardH = (PAGE.h - MARGIN * 2 - ROW_GAP * (ROWS - 1)) / ROWS;

  students.forEach((student, i) => {
    const posOnPage = i % CARDS_PER_PAGE;
    if (i > 0 && posOnPage === 0) doc.addPage();

    const col = posOnPage % COLS;
    const row = Math.floor(posOnPage / COLS);
    const x = MARGIN + col * (cardW + COL_GAP);
    const y = MARGIN + row * (cardH + ROW_GAP);

    drawCard(doc, student, x, y, cardW, cardH);
  });

  doc.save(`${branchName || "Branch"} - LMS Credentials.pdf`);
}
